<template>
  <!-- Top Bar -->
  <div>
    <v-app-bar flat color="primary" dark class="px-4">
      <v-autocomplete
        ref="productSearch"
        v-model="searchProduct"
        :items="listProduct"
        placeholder="Tìm hàng hóa"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-selected
        hide-details
        hide-no-data
        hide-spin-buttons
        item-text="name"
        item-value="id"
        class="search-bar"
        no-filter
        :search-input.sync="searchQuery"
        :loading="isSearching"
        return-object
        clearable
      >
        <template v-slot:item="{ item }">
          <v-list-item @click="addProductToOrder(item)">
            <v-list-item-content>
              <v-list-item-title>
                <span class="font-weight-medium">{{ item.name }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ formatCurrency(item.retail_cost) }} · Có thể bán: {{ item.temporality }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon 
            small 
            class="mr-2" 
            v-bind="attrs" 
            v-on="on"
          >
            mdi-information
          </v-icon>
        </template>
        <span>Bạn có thể thêm cùng một sản phẩm nhiều lần<br/>để có dòng bán và dòng tặng riêng biệt</span>
      </v-tooltip>
      <v-btn icon @click="fetchProducts('', true)">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn icon>
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-app-bar>

    <v-row no-gutters class="flex-grow-1">
      <v-col cols="12" md="8" class="product-list">
        <v-container class="fill-height" fluid
          style="align-items: baseline;"
        >
          <v-row align="center" justify="center">
            <v-col v-if="selectedProducts?.length === 0"
            class="text-center"
            cols="12">
              <v-spacer></v-spacer>
              Chưa có sản phẩm nào trong đơn hàng
              <v-spacer></v-spacer>
            </v-col>
            <v-col
              class=" px-0 py-0 mx-0 py-0"
              v-if="selectedProducts?.length > 0"
              cols="12"
              v-for="(item, index) in selectedProducts"
              :key="item._uniqueKey || index"
            >

              <v-card outlined class="product-order-card" :class="{'gift-item': item.is_gift}">
                <v-card-title class="product-order-card__content">
                  <v-row justify="space-between" align="center">
                    <v-col cols="1">
                      <span>{{ index + 1 }}</span>
                    </v-col>
                    <v-col cols="1">
                      <v-row align="center">
                        <v-icon @click="removeProduct(item)">
                          mdi-delete
                        </v-icon>
                      </v-row>
                    </v-col>
                    <v-col cols="2">
                      <v-row align="center">
                        <span class="ml-2">{{ item.sku }}</span>
                      </v-row>
                    </v-col>
                    <v-col cols="2" class="product-name-column">
                      <v-row align="center">
                        <span class="product-name" :title="item.name">{{ item.name }}</span>
                        <v-chip 
                          v-if="item.is_gift" 
                          x-small 
                          color="success" 
                          class="product-gift-chip"
                        >
                          Tặng
                        </v-chip>
                      </v-row>
                    </v-col>
                    <v-col cols="1" class="text-right">
                      <v-text-field
                        v-model="item.quantity"
                        type="number"
                        min="1"
                        dense
                        :rules="[quantityRules.positive]"
                        hide-spin-buttons
                        label="SL"
                        @input="validateQuantity(item)"
                        @keypress="preventNegativeNumber"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="1" class="text-center gift-checkbox-column">
                      <v-checkbox
                        v-model="item.is_gift"
                        label="Tặng"
                        dense
                        hide-details
                        @change="onGiftStatusChange(item)"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="2" class="text-right">
                      <v-text-field
                        v-if="!item.is_gift"
                        placeholder="Chiết khấu"
                        label="CK"
                        :value="item.formattedDiscount || '0'"
                        min="0"
                        dense
                        :rules="item.discountType === 1 ? [discountRules.percentage] : []"
                        hide-spin-buttons
                        @input="onProductDiscountInput(item, $event)"
                        @blur="formatProductDiscountOnBlur(item)"
                        @focus="onProductDiscountFocus(item)"
                      >
                        <template v-slot:append>
                          <v-btn style="margin-left: 0.3rem; margin-bottom: 0.5rem"
                                 outlined
                                 :rounded="false"
                                 height="25px"
                                 width="25px"
                                 text
                                 icon
                                 depressed
                                 @click="item.discountType = 1"
                                 :class="{'button-discount' : item.discountType === 1}"
                          >%
                          </v-btn>
                          <v-btn style="margin-left: 0.3rem; margin-bottom: 0.5rem"
                                 outlined
                                 :rounded="false"
                                 depressed
                                 height="25px"
                                 width="25px"
                                 text
                                 icon
                                 @click="item.discountType = 2"
                                 :class="{'button-discount' : item.discountType === 2}"
                          >đ
                          </v-btn>
                        </template>
                      </v-text-field>
                      <span v-else class="grey--text">Không áp dụng</span>
                    </v-col>
                    <v-col cols="2" class="text-right">
                      <span v-if="item.is_gift" class="success--text font-weight-bold">
                        MIỄN PHÍ
                      </span>
                      <span v-else>
                        {{formatCurrency(getProductPrice(item))}}
                      </span>
                    </v-col>
                  </v-row>
                </v-card-title>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-col>

      <!-- Order Summary Section -->
      <v-col cols="12" md="4" class="order-summary pa-4">
        <v-autocomplete
          v-model="selectedCustomerId"
          :items="listCustomer"
          placeholder="Tìm khách hàng"
          prepend-inner-icon="mdi-account"
          outlined
          dense
          hide-details
          item-text="displayText"
          item-value="id"
        ></v-autocomplete>

        <v-container class="order-details mt-4 pa-0">
          <v-row>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="6">
                  <span>Tổng tiền hàng</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  {{ formatCurrency(totalAmount) }}
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="6">
                  <span>Giảm giá</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <div class="discount-input-group">
                    <v-text-field
                      v-model="formattedOrderDiscount"
                      min="0"
                      dense
                      :rules="orderDiscountType === 1 ? [discountRules.percentage] : []"
                      hide-spin-buttons
                      class="discount-input"
                      placeholder="Nhập giảm giá"
                      @input="onOrderDiscountInput"
                      @blur="formatOrderDiscountOnBlur"
                      @focus="onOrderDiscountFocus"
                    ></v-text-field>
                    <div class="discount-type-buttons">
                      <v-btn
                        small
                        outlined
                        :color="orderDiscountType === 1 ? 'primary' : ''"
                        :class="{'active-discount-type': orderDiscountType === 1}"
                        @click="orderDiscountType = 1"
                      >%</v-btn>
                      <v-btn
                        small
                        outlined
                        :color="orderDiscountType === 2 ? 'primary' : ''"
                        :class="{'active-discount-type': orderDiscountType === 2}"
                        @click="orderDiscountType = 2"
                      >đ</v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="6">
                  <span>Khách cần trả</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  {{ formatCurrency(amountDue) }}
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="6" justify="center">
                  <span>Khách đã trả</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-text-field
                    placeholder="Nhập số tiền"
                    v-model="formattedAmountPaid"
                    dense
                    hide-details
                    @input="onAmountPaidInput"
                    @blur="formatAmountPaidOnBlur"
                    @focus="onAmountPaidFocus"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12"
            >
              <v-row justify="space-between">
                <v-col cols="6" justify="center">
                  <span>Trạng thái</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-select
                  v-model="orderStatus"
                    :items="[
                      { text: 'Thành công', value: 2 },
                      { text: 'Chờ xác nhận', value: 1 },
                    ]"
                    dense
                    hide-details
                    outlined
                    placeholder="Chọn trạng thái"
                  >

                  </v-select>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="6" justify="center">
                  <span>Ngày tạo đơn</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    offset-overflow
                    :nudge-left="$vuetify.breakpoint.smAndUp ? 120 : 0"
                    min-width="auto"
                    max-width="290"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedOrderDate"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="orderDate"
                      @input="dateMenu = false"
                      no-title
                      scrollable
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="6">
                  <span>{{ changeLabel }}</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  {{ formatCurrency(change) }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>

        <v-btn
          color="primary"
          block
          large
          class="mt-4"
          @click="processPayment"
          :disabled="selectedProducts.length === 0"
        >
          Thanh toán
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import productModule from '@/api/products';
const productActions = productModule.actions
import customerModule from '@/api/customers'
const customerActions = customerModule.actions
import orderModule from '@/api/order'
const orderActions = orderModule.actions
import { printHtmlContent } from '@/utils/print';

export default {
  name: "OrderSales",
  data() {
    return {
      searchProduct: "",
      searchCustomer: "",
      discountType: 0,  // 0 for amount, 1 for percentage
      amountPaid: 0,    // Amount paid by customer
      formattedAmountPaid: "0", // Formatted display value for amount paid
      listProduct: [],
      listCustomer: [],
      selectedProducts: [],
      selectedCustomerId: null,
      orderStatus: 2,
      orderId: null,
      htmlPrint: null,
      orderDiscount: 0, // New order-level discount value
      formattedOrderDiscount: "0", // Formatted display value for order discount
      orderDiscountType: 2, // Default to fixed amount (2 for VND, 1 for percentage)
      searchQuery: '',
      isSearching: false,
      debounceTimeout: null,
      productSearchController: null,
      lastProductSearch: null,
      orderDate: new Date().toISOString().substr(0, 10), // Default to current date (YYYY-MM-DD)
      dateMenu: false, // Control date picker menu
      // Validation rules
      quantityRules: {
        positive: value => value > 0 || 'Số lượng phải lớn hơn 0'
      },
      discountRules: {
        percentage: value => parseFloat(value) <= 100 || 'Chiết khấu không được vượt quá 100%'
      }
    };
  },
  mounted() {
    this.fetchProducts('', true);
    this.fetchCustomers();

    // Initialize formatted values
    this.formattedAmountPaid = this.formatNumber(this.amountPaid);
    this.formattedOrderDiscount = this.formatNumber(this.orderDiscount);

    // Initialize formatted values for any existing products
    if (this.selectedProducts && this.selectedProducts.length > 0) {
      this.selectedProducts.forEach(product => {
        product.formattedDiscount = this.formatNumber(product.discount || 0);
      });
    }
  },
  watch: {
    // Theo dõi sự thay đổi của searchQuery để gọi API tìm kiếm
    searchQuery(newValue) {
      // Hủy timeout trước đó nếu có
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      const normalizedSearch = (newValue || '').trim();

      // Không tải lại danh sách mặc định khi autocomplete tự xóa sau khi chọn.
      if (!normalizedSearch) {
        return;
      }

      // Chờ người dùng ngừng gõ để tránh tạo nhiều request chồng nhau.
      this.debounceTimeout = setTimeout(() => {
        this.fetchProducts(normalizedSearch);
      }, 350);
    },

    // Keep formatted values in sync with actual values
    amountPaid(newVal) {
      if (document.activeElement !== this.$el.querySelector('input[placeholder="Nhập số tiền"]')) {
        this.formattedAmountPaid = this.formatNumber(newVal);
      }
    },

    orderDiscount(newVal) {
      if (document.activeElement !== this.$el.querySelector('input[placeholder="Nhập giảm giá"]')) {
        this.formattedOrderDiscount = this.formatNumber(newVal);
      }
    }
  },
  beforeDestroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    if (this.productSearchController) {
      this.productSearchController.abort();
    }
  },
  computed: {
    // Calculate the sum of product prices after applying individual product discounts
    totalProductsAfterDiscount() {
      return this.selectedProducts.reduce((sum, product) => {
        // Không tính hàng tặng vào tổng tiền
        if (product.is_gift) {
          return sum;
        }
        return sum + this.getProductPrice(product);
      }, 0);
    },

    // Calculate the total amount from all selected products before any discounts
    totalAmount() {
      return this.selectedProducts.reduce((sum, product) => {
        // Không tính hàng tặng vào tổng tiền
        if (product.is_gift) {
          return sum;
        }
        return sum + (product.retail_cost * product.quantity);
      }, 0);
    },

    // Calculate the order-level discount amount
    orderDiscountAmount() {
      const orderDiscountValue = parseFloat(this.orderDiscount) || 0;

      if (this.orderDiscountType === 1) {
        // Percentage discount
        return (this.totalProductsAfterDiscount * orderDiscountValue) / 100;
      } else {
        // Fixed amount discount
        return orderDiscountValue;
      }
    },

    // Calculate the final amount customer needs to pay (products after discount minus order discount)
    amountDue() {
      return Math.max(0, this.totalProductsAfterDiscount - this.orderDiscountAmount);
    },

    // Calculate change to return to the customer
    change() {
      const paid = parseFloat(this.amountPaid) || 0;
      return paid - this.amountDue;
    },

    changeLabel() {
      return this.change >= 0 ? "Tiền thừa trả khách" : "Khách cần trả thêm";
    },

    // Format order date for display (DD/MM/YYYY)
    formattedOrderDate() {
      if (!this.orderDate) return '';
      const [year, month, day] = this.orderDate.split('-');
      return `${day}/${month}/${year}`;
    }
  },
  methods: {
    // Phương thức mới để xử lý khi thay đổi trạng thái hàng tặng
    onGiftStatusChange(item) {
      if (item.is_gift) {
        // Nếu là hàng tặng, reset discount về 0
        item.discount = 0;
        item.formattedDiscount = '0';
      }
    },
    
    // Phương thức mới để tìm kiếm sản phẩm theo từ khóa
    getProductPrice(item) {
      item.discount = parseFloat(item.discount) || 0;
      const unitPrice = item.retail_cost;
      const quantity = parseInt(item.quantity) || 1;
      if (item.discountType === 1) {
        // Percentage discount
        return (unitPrice - (unitPrice * item.discount / 100)) * quantity;
      } else if (item.discountType === 2) {
        // Fixed amount discount
        return (unitPrice - item.discount) * quantity;
      }
      return unitPrice * quantity;
    },
    removeProduct(item) {
      // Sử dụng _uniqueKey để xóa đúng dòng sản phẩm
      const index = this.selectedProducts.findIndex(product => 
        product._uniqueKey ? product._uniqueKey === item._uniqueKey : product.id === item.id
      );
      if (index !== -1) {
        this.selectedProducts.splice(index, 1);
      }
    },
    formatCurrency(value) {
      return value.toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      });
    },

    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    parseFormattedNumber(formattedValue) {
      return parseFloat(formattedValue.toString().replace(/[^\d.-]/g, '')) || 0;
    },

    // Amount Paid formatting methods
    onAmountPaidInput(value) {
      // In Vuetify's v-text-field, the input event handler receives the value directly
      const numericValue = value ? value.toString().replace(/[^\d]/g, '') : '';
      this.amountPaid = parseFloat(numericValue) || 0;
      this.formattedAmountPaid = this.formatNumber(this.amountPaid);
    },

    formatAmountPaidOnBlur() {
      this.formattedAmountPaid = this.formatNumber(this.amountPaid);
    },

    onAmountPaidFocus() {
      this.formattedAmountPaid = this.amountPaid.toString();
    },

    // Order Discount formatting methods
    onOrderDiscountInput(value) {
      // In Vuetify's v-text-field, the input event handler receives the value directly
      const numericValue = value ? value.toString().replace(/[^\d]/g, '') : '';
      this.orderDiscount = parseFloat(numericValue) || 0;

      // Validate percentage discount does not exceed 100%
      if (this.orderDiscountType === 1 && this.orderDiscount > 100) {
        this.orderDiscount = 100;
        this.$snackbar.warning('Chiết khấu theo phần trăm không được vượt quá 100%');
      }

      this.formattedOrderDiscount = this.formatNumber(this.orderDiscount);
    },

    formatOrderDiscountOnBlur() {
      this.formattedOrderDiscount = this.formatNumber(this.orderDiscount);
    },

    onOrderDiscountFocus() {
      this.formattedOrderDiscount = this.orderDiscount.toString();
    },

    // Product discount formatting methods
    onProductDiscountInput(product, value) {
      // In Vuetify's v-text-field, the input event handler receives the value directly
      const numericValue = value ? value.toString().replace(/[^\d]/g, '') : '';
      product.discount = parseFloat(numericValue) || 0;

      // Validate percentage discount does not exceed 100%
      if (product.discountType === 1 && product.discount > 100) {
        product.discount = 100;
        this.$snackbar.warning('Chiết khấu theo phần trăm không được vượt quá 100%');
      }

      product.formattedDiscount = this.formatNumber(product.discount);
    },

    formatProductDiscountOnBlur(product) {
      product.formattedDiscount = this.formatNumber(product.discount || 0);
    },

    onProductDiscountFocus(product) {
      product.formattedDiscount = (product.discount || 0).toString();
    },
    processPayment() {
      const isPaidLessThanDue = parseFloat(this.amountPaid) < this.amountDue;

      if (isPaidLessThanDue && !this.selectedCustomerId) {
        this.$snackbar.error("Đơn hàng nợ cần có thông tin khách hàng. Vui lòng chọn khách hàng trước khi thanh toán.");
        return;
      }

      const orderDetails = this.processOrderDetails();
      let orderData = this.mergeOrderData();

      orderData.order_detail = orderDetails;
      orderActions.createOrder(orderData)
        .then(response => {
          if (response.status === 200) {
            this.orderId = response.data.data.id;
            this.$snackbar.success("Đơn hàng đã được tạo thành công");
            this.printOrder();
            this.reset();
          }
          else{
            this.$snackbar.error("Đã có lỗi xảy ra trong quá trình tạo đơn hàng");
            console.error("Error creating order:", response);
          }
        })
    },
    reset(){
      this.selectedProducts = [];
      this.selectedCustomerId = null;
      this.amountPaid = 0;
      this.formattedAmountPaid = "0";
      this.orderStatus = 2;
      this.searchProduct = "";
      this.searchCustomer = "";
      this.discount = 0;
      this.discountType = 0;
      this.orderDiscount = 0;
      this.formattedOrderDiscount = "0";
      this.orderDiscountType = 2;
      this.orderDate = new Date().toISOString().substr(0, 10); // Reset to current date
      this.listProduct = [];
      this.listCustomer = [];
      this.fetchProducts('', true);
      this.fetchCustomers();
    },
    printOrder() {
      orderActions.printOrder(this.orderId).then(response => {
        if (response.status === 200) {
          console.log("Order printed successfully");
          this.htmlPrint = response;
          printHtmlContent(this.htmlPrint.data); // Use the utility function here
          this.$snackbar.success("In đơn hàng thành công");
        } else {
          console.error("Error printing order:", response);
          this.$snackbar.error("Đã có lỗi xảy ra trong quá trình in đơn hàng");
        }
      })
      .catch(error => {
        console.error("Error printing order:", error);
        this.$snackbar.error("Đã có lỗi xảy ra trong quá trình in đơn hàng");
      });
    },
    mergeOrderData() {
      const payment = {
        price: this.amountPaid,
        type: 1,
      };
      const orderData = {
        customer_id: this.selectedCustomerId,
        payment: payment,
        is_retail: true,
        discount: this.orderDiscount,
        discount_type: this.orderDiscountType,
        total_amount: this.totalProductsAfterDiscount,
        final_amount: this.amountDue,
        status: this.orderStatus,
        type: 1,
        create_date: this.orderDate, // Backend expects 'create_date' field
      };
      return orderData;
    },
    processOrderDetails() {
      let orderDetails = [];
      this.selectedProducts.forEach((item) => {
        const product = {
          product_id: item.id,
          quantity: item.quantity,
          discount: item.is_gift ? 0 : item.discount, // Hàng tặng không có chiết khấu
          discount_type: item.discountType,
          price: item.retail_cost,
          is_gift: item.is_gift ? 1 : 0, // Gửi trạng thái hàng tặng
        };
        orderDetails.push(product);
      });
      return orderDetails;
    },
    fetchProducts(searchTerm = '', force = false) {
      const normalizedSearch = typeof searchTerm === 'string'
        ? searchTerm.trim()
        : '';

      if (!force && normalizedSearch === this.lastProductSearch) {
        return;
      }

      if (this.productSearchController) {
        this.productSearchController.abort();
      }
      this.productSearchController = new AbortController();
      const currentController = this.productSearchController;
      this.lastProductSearch = normalizedSearch;

      const payload = {
        per_page: 12,
        name: normalizedSearch,
        signal: currentController.signal,
      };

      this.isSearching = true;

      productActions.getProductsV2(payload)
        .then((response) => {
          this.listProduct = response.data.data.map((item) => ({
            ...item,
            discountType: item.discount_type || 1,
            discount: item.discount || 0,
          }));
        })
        .catch((error) => {
          if (error.code === 'ERR_CANCELED') {
            return;
          }
          this.lastProductSearch = null;
          console.error("Error fetching products:", error);
          this.$snackbar.error("Không thể tìm kiếm sản phẩm");
        })
        .finally(() => {
          // Request cũ không được phép tắt loading của request mới hơn.
          if (this.productSearchController === currentController) {
            this.isSearching = false;
            this.productSearchController = null;
          }
        });
    },
    fetchCustomers(){
      const payload = {
        per_page: 1000,
        search: this.searchCustomer,
      };
      customerActions.getCustomers(payload)
        .then((response) => {
         this.listCustomer = response.data.data.map(customer => ({
            ...customer,
            displayText: `${customer.name} - ${customer.phone}`
          }));
          console.log("Fetched customers:", this.listCustomer);
        })
        .catch((error) => {
          console.error("Error fetching customers:", error);
        });
    },
    addProductToOrder(item) {
      // Check if product can be sold (not negative inventory)
      const selectedProduct = this.listProduct.find(product => product.id === item.id);
      
      // Tính tổng số lượng sản phẩm này trong đơn (cả bán và tặng)
      const totalQuantityInOrder = this.selectedProducts
        .filter(product => product.id === item.id)
        .reduce((sum, product) => sum + product.quantity, 0);

      // Check inventory constraints
      if (selectedProduct.is_buy_always && selectedProduct.temporality <= totalQuantityInOrder) {
        this.$snackbar.warning('Sản phẩm này không thể bán âm!');
        return;
      }

      // Luôn thêm sản phẩm mới thay vì gộp (để có thể có nhiều dòng cùng sản phẩm)
      // Tạo unique key để phân biệt các dòng
      const uniqueKey = Date.now() + Math.random();
      const newProduct = {...item, quantity: 1, _uniqueKey: uniqueKey};
      
      // Use discount from product configuration
      newProduct.discountType = item.discount_type || item.discountType || 1;
      newProduct.discount = item.discount || 0;
      newProduct.formattedDiscount = this.formatNumber(newProduct.discount);
      newProduct.is_gift = false; // Khởi tạo trạng thái không phải hàng tặng
      this.selectedProducts.push(newProduct);

      // Clear search to allow selecting another product
      this.searchProduct = null;
      this.searchQuery = '';
      
      // Close the dropdown menu
      this.$nextTick(() => {
        if (this.$refs.productSearch) {
          this.$refs.productSearch.blur();
          this.$refs.productSearch.isMenuActive = false;
        }
      });
    },

    validateQuantity(item) {
      // Ensure quantity is always a positive number
      const quantity = parseFloat(item.quantity);
      if (quantity <= 0 || isNaN(quantity)) {
        item.quantity = 1;
        this.$snackbar.warning('Số lượng sản phẩm phải lớn hơn 0');
      }
    },

    preventNegativeNumber(evt) {
      // Prevent typing negative sign (-) in the quantity field
      if (evt.key === '-' || evt.key === 'e' || evt.key === '+') {
        evt.preventDefault();
      }
    }
  },
};
</script>

<style scoped>
.order-create-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.search-bar {
  max-width: 400px;

}

.v-input__slot {
  color: #010832 !important;
  border-color: #3c007c !important;
}

.product-list {
  background-color: #f5f5f5;
  border-right: 1px solid #e0e0e0;
}

.order-summary {
  background-color: white;
}

.order-id {
  font-size: 1.2rem;
  font-weight: bold;
}
.button-discount {
  background-color: #3c007c !important;
  color: white !important;
}

.product-order-card__content {
  padding: 10px 14px !important;
}

.product-order-card__content > .row {
  min-height: 58px;
}

.product-name-column {
  min-width: 0;
}

.product-name {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: 14px;
  line-height: 1.25;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.product-gift-chip {
  margin-top: 4px;
}

.gift-checkbox-column {
  padding-left: 4px !important;
  padding-right: 4px !important;
}

.gift-checkbox-column .v-input {
  margin: 0;
  justify-content: center;
}

.gift-checkbox-column >>> .v-label {
  max-width: none;
  margin-left: 3px;
  font-size: 12px;
  white-space: nowrap;
}

.gift-checkbox-column >>> .v-input--selection-controls__input {
  margin-right: 0;
}

/* New styles for discount input group */
.discount-input-group {
  display: flex;
  align-items: center;
  justify-content: end;
}

.discount-input {
  max-width: 100px;
}

.discount-type-buttons {
  display: flex;
  margin-left: 8px;
}

.active-discount-type {
  background-color: #61cf67 !important;
  color: rgb(164, 17, 184) !important;
}

/* Style cho hàng tặng */
.gift-item {
  background-color: #f1f8f4 !important;
  border-left: 4px solid #4caf50 !important;
}
</style>
